import {
  IsArray,
  IsBoolean,
  IsDateString,
  IsIn,
  IsInt,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
} from 'class-validator';
import { FILE_STAGES, type FileStage } from './files.dto';

/**
 * The notice/consent determination the officer makes before the first
 * inspection — "It must be determined whether notice is required or consent must
 * be obtained" (best practices, Step 1 item 3). The six options are the spec's
 * lettered list a–f, all under LGA s. 144, and the code carries the subsection so
 * the statutory basis is legible in stored data rather than only in the label.
 *
 * A caveat worth knowing when reading a file: only (a), (b), (e) and (f) are
 * genuinely alternative *routes* onto the property. (c) producing identification
 * on request and (d) being accompanied by a specialist are, in the statute, a
 * duty and a power that attach to an inspection however it is authorized — they
 * are not exclusive of the others. They are offered as determinations here
 * because the spec enumerates all six as one list; where an officer records (c)
 * or (d), the underlying route is normally still notice or consent.
 */
export type EntryDetermination =
  | 'notice_144_2_a'
  | 'consent_144_6'
  | 'identification_144_7'
  | 'accompanied_144_8'
  | 'emergency_144_9'
  | 'warrant_144_10';
export const ENTRY_DETERMINATIONS: EntryDetermination[] = [
  'notice_144_2_a',
  'consent_144_6',
  'identification_144_7',
  'accompanied_144_8',
  'emergency_144_9',
  'warrant_144_10',
];

/**
 * The determinations that route the file somewhere other than straight to the
 * site visit. A warrant holds the file at review until the entry-warrant
 * sub-flow resolves (s. 144(10)); an emergency is the s. 144(9) bypass, which
 * the flowchart routes to Step 5 and/or Step 8. Kept as data next to the union
 * so both API surfaces branch on one list instead of two copies of an `if`.
 */
export const ENTRY_DETERMINATIONS_REQUIRING_WARRANT: EntryDetermination[] = ['warrant_144_10'];
export const ENTRY_DETERMINATIONS_EMERGENCY: EntryDetermination[] = ['emergency_144_9'];

/** Stage 2 — officer review & pre-visit planning. */
export interface ReviewStageData {
  isDwelling?: 'yes' | 'no' | 'unknown';
  /**
   * The structured notice/consent determination. Supersedes the free-text
   * `entryAuthority` below, which is retained as-is: it holds officer prose
   * on existing files that no automatic mapping could turn into one of the six
   * codes without guessing, and this is an evidentiary record.
   */
  entryDetermination?: EntryDetermination;
  /**
   * Why dwelling status could not be determined, when `isDwelling` is
   * `unknown`. Whether the property is a dwelling governs the entry authority
   * the officer needs, so an unexplained `unknown` blocks the next decision.
   */
  isDwellingOther?: string;
  entryAuthority?: string;
  preVisitContact?: 'phone' | 'letter' | 'none';
  notes?: string;
}

/**
 * Stage 3 — site visit & evidence gathering. The `offenseDate` is officer-entered
 * (fact-driven, not auto-calculated) and starts the six-month Provincial Court
 * proceedings limitation clock. The statutory-assessment flags mirror the paper
 * inspection report's checkboxes.
 */
export interface SiteVisitStageData {
  observations?: string;
  substantiated?: boolean;
  accessRefused?: boolean;
  // Officer-entered date the offense occurred — drives the six-month proceedings
  // limitation clock (POPA s. 21(1)). Distinct from the inspection date.
  offenseDate?: string; // ISO date
  inspectionDate?: string; // ISO date the visit occurred
  // Statutory assessment (one or more) — mirrors the inspection-report checkboxes.
  assessment131_1?: boolean; // s.131(1) objects on premises
  assessment131_2?: boolean; // s.131(2) building/structure danger
  assessment131_3?: boolean; // s.131(3) dilapidation/structural soundness
  assessment139?: boolean; // s.139 emergency situation
  // Verbal notice given on-site → written follow-up (courtesy letter) required.
  informalNotice?: boolean;
  // Field cost estimate — re-captured/adjusted at Stage 4; both values retained.
  estimatedCost?: string;
  notes?: string;
}

/** Stage 4 — classification & compliance. */
export type Contravention = 'lga_131_1' | 'lga_131_2' | 'lga_131_3';
export const CONTRAVENTIONS: Contravention[] = ['lga_131_1', 'lga_131_2', 'lga_131_3'];

/**
 * What the Stage 4 analysis concluded. Wider than {@link Contravention}, which
 * only names a subsection: the analysis can also conclude that the premises
 * comply, or that the matter is a s. 139 emergency assessment. Those two are
 * outcomes the file has always been able to reach in the spec but had nowhere to
 * record, so a compliant property had to be classified as contravening something.
 *
 * From the best-practices spec's Step 2 SCENARIOS:
 *   a. compliance with the by-law                          → file CLOSED
 *   b. s. 131(1) prescribed objects on the premises         → proceed
 *   c. s. 131(2) building dangerous (uninhabited/unoccupied)→ proceed
 *   d. s. 131(3) dilapidation / structural unsoundness      → expert report first
 *   e. s. 139 emergency analysis                            → see `classificationRoute`
 */
export type ClassificationOutcome = 'compliant' | Contravention | 'lga_139';
export const CLASSIFICATION_OUTCOMES: ClassificationOutcome[] = [
  'compliant',
  'lga_131_1',
  'lga_131_2',
  'lga_131_3',
  'lga_139',
];

/**
 * The remedial measures the spec names, per subsection:
 *   s. 131(1) — "repair or clean up"                    (Step 2 scenario b)
 *   s. 131(2) — "repair or demolish"                    (Step 2 scenario c)
 *   s. 131(3) — "demolish all or part of the building"  (Step 3 item 2)
 * Recorded as a set: a file can genuinely need more than one, and the spec
 * speaks of "the measures" in the plural.
 */
export type RemedialMeasure = 'repair' | 'clean_up' | 'demolish';
export const REMEDIAL_MEASURES: RemedialMeasure[] = ['repair', 'clean_up', 'demolish'];

/**
 * Which measures each subsection can carry. Enforced on both API surfaces so a
 * file never records, say, a clean-up under s. 131(2) — the subsection is what
 * the municipality has to justify on appeal, and the measures are the relief it
 * is asking for.
 */
export const MEASURES_BY_CONTRAVENTION: Record<Contravention, RemedialMeasure[]> = {
  lga_131_1: ['repair', 'clean_up'],
  lga_131_2: ['repair', 'demolish'],
  lga_131_3: ['demolish'],
};

/**
 * Where a classification sends the file. Derived from the spec rather than
 * invented, and shared by both API surfaces so the two cannot disagree about a
 * routing decision with statutory consequences.
 *
 *   `close`     — nothing to enforce. Spec Step 2 scenario a (compliance) and
 *                 scenario e.i (s. 139 analysed, no emergency found).
 *   `emergency` — spec Step 2 scenario e.ii: work carried out to remove the
 *                 danger (Step 8) and/or a notice to comply served (Step 5).
 *   `escalate`  — the ordinary path to the escalation type selector.
 *
 * The emergency flag is checked before the contravention because the v47
 * flowchart raises the s. 144(9) bypass from Stage 4 generally ("Emergency flag
 * (s. 144(9)) if applicable"), not only from a s. 139 analysis. The mechanics of
 * that bypass — its dual entry and compressed deadline — are not modelled here.
 */
export type ClassificationRoute = 'close' | 'escalate' | 'emergency';

export function classificationRoute(
  outcome: ClassificationOutcome | undefined,
  emergency: boolean,
): ClassificationRoute {
  // Compliance closes the file whatever else is set: there is no contravention
  // to enforce, so there is nothing an emergency could accelerate.
  if (outcome === 'compliant') return 'close';
  if (emergency) return 'emergency';
  // s. 139 analysed and no emergency found — spec Step 2 scenario e.i.
  if (outcome === 'lga_139') return 'close';
  return 'escalate';
}

export interface ClassificationStageData {
  /**
   * What the analysis concluded. Supersedes `contravention` below, which is kept
   * in step with it by the API (populated for the three subsections, cleared
   * otherwise) so existing readers — document assembly, the mobile aggregate —
   * keep working unchanged.
   *
   * Named in full because the stage DTO is one flat bag across every stage, and
   * a bare `outcome` there already means the Step 9 proceedings result.
   */
  classificationOutcome?: ClassificationOutcome;
  contravention?: Contravention;
  expertReportRef?: string;
  correctiveWork?: string;
  complianceDeadline?: string; // ISO date
  emergency?: boolean;

  // ── Structured s. 131 assessment (Step 2 scenarios b/c, Step 3 item 2) ─────
  /** Repair / clean up / demolish — constrained by the subsection. */
  remedialMeasures?: RemedialMeasure[];
  /** "the anticipated costs" — free text, matching Stage 3's `estimatedCost`. */
  anticipatedCost?: string;
  /**
   * "the potential value of the objects" — s. 131(1) only. The other two
   * subsections concern a building or structure, which has no equivalent.
   */
  objectValue?: string;
  /** "the reasonable time frame to correct the contravention", in days. */
  timeToCompleteDays?: number;
  /**
   * The date set for the next inspection — spec Step 4 item 1, "A date is set
   * for the next inspection of the property". Distinct from
   * `complianceDeadline`, which is the date the work must be finished by: the
   * officer returns to look, which may be before or after the deadline.
   */
  returnInspectionDate?: string; // ISO date

  notes?: string;
}

/**
 * Escalation entry point — the officer selects the escalation type at the end
 * of Stage 4 and records the authorization that travels with the file. For the
 * D&U workflow the only available type is Type 3 (Form 4: Notice to Comply);
 * the other nine hub types are out of scope for v47. The Steps 5–9 sub-flow
 * tracked beyond this point is owned by the escalation contracts.
 */
export type EscalationType = 'type_3';
export interface EscalationStageData {
  escalationType?: EscalationType;
  // Who authorized the escalation (name and/or role) and when — the
  // authorization record that travels with the file (LGA s. 137).
  authorizedBy?: string;
  authorizationDate?: string; // ISO date
  authorizationRef?: string; // council resolution / delegation reference
  notes?: string;
}

/**
 * Type 3 sub-flow, Step 5 — Form 4: Notice to Comply (LGA s. 134). The Form 4
 * itself pre-populates from the file record (no re-entry); this slice captures
 * the officer/clerk execution state around it. The 14-day appeal clock is
 * derived from `serviceDate`, not stored. Steps 5–9 run while the file-level
 * stage stays at `escalation`, so each step persists under its own stageData key.
 */
export type ClerkReviewStatus = 'pending' | 'reviewed';
/**
 * The four service methods (Step 5.3). `posted` is posting in a conspicuous
 * place on the property, `in_person` is personal/hand delivery.
 */
export type ServiceMethod = 'in_person' | 'mail' | 'posted' | 'email';
export const SERVICE_METHODS: ServiceMethod[] = ['posted', 'in_person', 'mail', 'email'];

export interface Step5Data {
  noticeDetails?: string; // remaining fields the officer completes on the notice
  clerkStatus?: ClerkReviewStatus;
  sealApplied?: boolean; // municipal seal applied
  sealDate?: string; // ISO date the corporate seal was applied
  registered?: boolean; // registered at the registry office
  registrationRef?: string;
  // Registration particulars (5.2) — s. 135 imputes notice to subsequent owners.
  registryOffice?: string;
  registrationDate?: string; // ISO date
  registrationNumber?: string;
  // Mirrors of the operative service record (5.3); the authoritative rows live
  // in `gov_service_record`. Kept here so the deadlines engine, checklist and
  // document assembly read one value.
  serviceMethod?: ServiceMethod;
  // ISO date — starts the 14-day appeal period (s. 134(1)). s. 134(2) is the
  // separate rule that the notice becomes final and binding if no appeal lands.
  serviceDate?: string;
  voluntaryInspection?: boolean; // sub-step 5.5 — optional compliance inspection
  accessRefused?: boolean; // 5.5 — triggers the entry-warrant sub-flow
  notes?: string;
}

/**
 * Type 3 sub-flow, Step 6 — Appeal Committee (LGA s. 135). Entered from the
 * 4.9 appeal gate: `appealFiled` records whether an appeal landed inside the
 * 14-day window. A `rescind` decision closes the file; confirm/modify/extend
 * proceed to Step 7 once the 14-day Court of King's Bench window (derived from
 * `decisionDate`) has run.
 */
export type StaffReportStatus = 'pending' | 'prepared';
export type CommitteeDecision = 'confirm' | 'modify' | 'extend' | 'rescind';
export interface Step6Data {
  appealFiled?: boolean;
  staffReportStatus?: StaffReportStatus;
  committeeDecision?: CommitteeDecision;
  decisionDate?: string; // ISO date — starts the King's Bench window (s. 135)
  /**
   * ISO date the owner must now comply by, when the committee varied the
   * notice (modify / extend). Written by the appeals module from the decision;
   * absent on a file whose appeal was recorded only through this screen.
   */
  revisedComplianceDeadline?: string;
  notes?: string;
}

/**
 * Type 3 sub-flow, Step 7 — Council intervention (LGA s. 137). Council may
 * authorize one or more actions: `refuse` closes the file; `work` routes to
 * Step 8; `proceedings` routes to Step 9. Work and proceedings may both be
 * authorized, and either path can return to Step 7 for re-deliberation. An
 * optional current-state inspection (reuse/refresh) and a finance-consultation
 * flag (when expenditure exceeds limits) are captured alongside.
 */
export type InspectionDecision = 'reuse' | 'refresh';
export type CouncilDecision = 'refuse' | 'work' | 'proceedings';
export interface Step7Data {
  inspectionDecision?: InspectionDecision;
  financeConsultation?: boolean;
  councilDecisions?: CouncilDecision[];
  notes?: string;
}

/**
 * Type 3 sub-flow, Step 8 — Clean up, demolition, repair & collection (LGA
 * s. 143). The municipality documents the work and recovers cost via municipal
 * collection (s. 143) or provincial reimbursement. On compliance with the debt
 * settled, a Form 5 Discharge Certificate is issued and the file closes. If the
 * owner refuses access for the work, the step pauses for an entry warrant.
 */
export type CollectionPath = 'municipal' | 'provincial';
export interface Step8Data {
  workDescription?: string;
  workBy?: string;
  workCost?: string;
  collectionPath?: CollectionPath;
  accessRefused?: boolean; // owner refused access for the work — pause for a warrant
  complianceMet?: boolean;
  debtSettled?: boolean;
  dischargeIssued?: boolean; // Form 5 Discharge Certificate issued
  dischargeDate?: string; // ISO date
  notes?: string;
}

/**
 * Type 3 sub-flow, Step 9 — Proceedings in Provincial Court (LGA s. 137 / s. 72
 * / s. 80). The officer files penal proceedings under a council mandate or a
 * standing delegation, then hands off to the Provincial Offences Procedure
 * Module (Epic P). Resolved closes the file; unresolved returns to Step 7.
 */
export type MandateSource = 'council_resolution' | 'standing_delegation';
export type ProceedingsOutcome = 'resolved' | 'unresolved';
export interface Step9Data {
  mandateSource?: MandateSource;
  filed?: boolean; // officer proceeded to file
  outcome?: ProceedingsOutcome;
  notes?: string;
}

/** The full per-file bag of stage data, keyed by stage. */
export interface StageDataBag {
  review?: ReviewStageData;
  site_visit?: SiteVisitStageData;
  classification?: ClassificationStageData;
  escalation?: EscalationStageData;
  step_5?: Step5Data;
  step_6?: Step6Data;
  step_7?: Step7Data;
  step_8?: Step8Data;
  step_9?: Step9Data;
}

/**
 * One flexible payload covering every stage's fields (all optional). The route
 * `:stage` param decides which slice is persisted; the global ValidationPipe
 * needs every accepted field declared, hence the single flat DTO.
 */
export class SaveStageDataDto {
  // review
  @IsOptional()
  @IsIn(['yes', 'no', 'unknown'])
  isDwelling?: 'yes' | 'no' | 'unknown';

  @IsOptional()
  @IsString()
  @MaxLength(200)
  isDwellingOther?: string;

  @IsOptional()
  @IsIn(ENTRY_DETERMINATIONS)
  entryDetermination?: EntryDetermination;

  @IsOptional()
  @IsString()
  entryAuthority?: string;

  @IsOptional()
  @IsIn(['phone', 'letter', 'none'])
  preVisitContact?: 'phone' | 'letter' | 'none';

  // site visit
  @IsOptional()
  @IsString()
  observations?: string;

  @IsOptional()
  @IsBoolean()
  substantiated?: boolean;

  @IsOptional()
  @IsBoolean()
  accessRefused?: boolean;

  @IsOptional()
  @IsDateString()
  offenseDate?: string;

  @IsOptional()
  @IsDateString()
  inspectionDate?: string;

  @IsOptional()
  @IsBoolean()
  assessment131_1?: boolean;

  @IsOptional()
  @IsBoolean()
  assessment131_2?: boolean;

  @IsOptional()
  @IsBoolean()
  assessment131_3?: boolean;

  @IsOptional()
  @IsBoolean()
  assessment139?: boolean;

  @IsOptional()
  @IsBoolean()
  informalNotice?: boolean;

  @IsOptional()
  @IsString()
  estimatedCost?: string;

  // classification
  @IsOptional()
  @IsIn(CLASSIFICATION_OUTCOMES)
  classificationOutcome?: ClassificationOutcome;

  @IsOptional()
  @IsIn(CONTRAVENTIONS)
  contravention?: Contravention;

  @IsOptional()
  @IsArray()
  @IsIn(REMEDIAL_MEASURES, { each: true })
  remedialMeasures?: RemedialMeasure[];

  @IsOptional()
  @IsString()
  @MaxLength(64)
  anticipatedCost?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  objectValue?: string;

  // A time frame is only "reasonable" if it is a real span of days; 0 would mean
  // immediate compliance, which is the emergency route, not a deadline.
  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(3650)
  timeToCompleteDays?: number;

  @IsOptional()
  @IsDateString()
  returnInspectionDate?: string;

  @IsOptional()
  @IsString()
  expertReportRef?: string;

  @IsOptional()
  @IsString()
  correctiveWork?: string;

  @IsOptional()
  @IsDateString()
  complianceDeadline?: string;

  @IsOptional()
  @IsBoolean()
  emergency?: boolean;

  // escalation
  @IsOptional()
  @IsIn(['type_3'])
  escalationType?: EscalationType;

  @IsOptional()
  @IsString()
  authorizedBy?: string;

  @IsOptional()
  @IsDateString()
  authorizationDate?: string;

  @IsOptional()
  @IsString()
  authorizationRef?: string;

  // step 5 — Form 4: Notice to Comply
  @IsOptional()
  @IsString()
  noticeDetails?: string;

  @IsOptional()
  @IsIn(['pending', 'reviewed'])
  clerkStatus?: ClerkReviewStatus;

  @IsOptional()
  @IsBoolean()
  sealApplied?: boolean;

  @IsOptional()
  @IsDateString()
  sealDate?: string;

  @IsOptional()
  @IsBoolean()
  registered?: boolean;

  @IsOptional()
  @IsString()
  registrationRef?: string;

  @IsOptional()
  @IsString()
  registryOffice?: string;

  @IsOptional()
  @IsDateString()
  registrationDate?: string;

  @IsOptional()
  @IsString()
  registrationNumber?: string;

  @IsOptional()
  @IsIn(SERVICE_METHODS)
  serviceMethod?: ServiceMethod;

  @IsOptional()
  @IsDateString()
  serviceDate?: string;

  @IsOptional()
  @IsBoolean()
  voluntaryInspection?: boolean;

  // step 6 — Appeal Committee
  @IsOptional()
  @IsBoolean()
  appealFiled?: boolean;

  @IsOptional()
  @IsIn(['pending', 'prepared'])
  staffReportStatus?: StaffReportStatus;

  @IsOptional()
  @IsIn(['confirm', 'modify', 'extend', 'rescind'])
  committeeDecision?: CommitteeDecision;

  @IsOptional()
  @IsDateString()
  decisionDate?: string;

  @IsOptional()
  @IsDateString()
  revisedComplianceDeadline?: string;

  // step 7 — Council intervention
  @IsOptional()
  @IsIn(['reuse', 'refresh'])
  inspectionDecision?: InspectionDecision;

  @IsOptional()
  @IsBoolean()
  financeConsultation?: boolean;

  @IsOptional()
  @IsArray()
  @IsIn(['refuse', 'work', 'proceedings'], { each: true })
  councilDecisions?: CouncilDecision[];

  // step 8 — Cleanup, repair & collection
  @IsOptional()
  @IsString()
  workDescription?: string;

  @IsOptional()
  @IsString()
  workBy?: string;

  @IsOptional()
  @IsString()
  workCost?: string;

  @IsOptional()
  @IsIn(['municipal', 'provincial'])
  collectionPath?: CollectionPath;

  @IsOptional()
  @IsBoolean()
  complianceMet?: boolean;

  @IsOptional()
  @IsBoolean()
  debtSettled?: boolean;

  @IsOptional()
  @IsBoolean()
  dischargeIssued?: boolean;

  @IsOptional()
  @IsDateString()
  dischargeDate?: string;

  // step 9 — Proceedings in Provincial Court
  @IsOptional()
  @IsIn(['council_resolution', 'standing_delegation'])
  mandateSource?: MandateSource;

  @IsOptional()
  @IsBoolean()
  filed?: boolean;

  @IsOptional()
  @IsIn(['resolved', 'unresolved'])
  outcome?: ProceedingsOutcome;

  // shared
  @IsOptional()
  @IsString()
  notes?: string;

  /** Advance the file to the next stage after saving. */
  @IsOptional()
  @IsBoolean()
  advance?: boolean;

  /**
   * The file-level stage the client believed the file was at when it rendered
   * the form. A mismatch is a 409 rather than a silent write: a second tab, or
   * a colleague who advanced the file meanwhile, would otherwise have this save
   * land on (and possibly advance) a stage the officer was not looking at.
   */
  @IsOptional()
  @IsIn(FILE_STAGES)
  expectedStage?: FileStage;
}

/**
 * Machine-readable reasons a workflow write is refused. Sent as `code` beside
 * the English `message` so a client can render its own translated sentence
 * rather than the server's.
 */
export type WorkflowErrorCode =
  /** The stage being written is not the stage the file is at. */
  | 'stage_mismatch'
  /** The file is closed; its stage record is final. */
  | 'file_closed'
  /** Steps 6–9 need the Form 4 notice served first (Step 5). */
  | 'notice_not_served'
  /** "No appeal filed" cannot be recorded until the 14-day window has run. */
  | 'appeal_window_open'
  /** A Form 5 discharge needs the date it was issued. */
  | 'discharge_date_required'
  /** Closing from escalation needs a terminal decision recorded at a step. */
  | 'closure_not_reached'
  /** The caller may record work but not move the file on. */
  | 'advance_forbidden'
  /** Step 8 remedial work needs council's authorization (or an emergency bypass). */
  | 'council_authorization_required';
